import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider
} from '@chakra-ui/react';
import { FiSearch, FiLogIn } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AdminPage = () => {
  const { user, token } = useAuth();
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Check if user is admin
  const isAdmin = user?.is_admin || false;

  const handleImpersonate = async (e) => {
    e.preventDefault();
    
    if (!userEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a user email',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Ensure token is in localStorage
      localStorage.setItem('token', token);

      // Call the impersonation endpoint
      const response = await api.post('/api/admin/impersonate', {
        target_email: userEmail.trim().toLowerCase()
      });

      const { access_token, user_email, user_name } = response.data;

      // Store the new access token
      localStorage.setItem('token', access_token);
      
      // Store impersonation info for UI banner
      localStorage.setItem('impersonating', JSON.stringify({
        email: user_email,
        name: user_name,
        admin_email: user.email
      }));

      toast({
        title: 'Success',
        description: `Now viewing as ${user_name} (${user_email})`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to home page to view campaigns
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);

    } catch (error) {
      console.error('Impersonation failed:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to impersonate user';
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExitImpersonation = () => {
    // Remove impersonation flag
    localStorage.removeItem('impersonating');
    
    toast({
      title: 'Exited Impersonation',
      description: 'You are now back to your admin account',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });

    // Reload to clear the user's session and return to admin view
    window.location.href = '/admin';
  };

  // Check if currently impersonating
  const impersonatingData = localStorage.getItem('impersonating');
  const isImpersonating = !!impersonatingData;
  const impersonationInfo = isImpersonating ? JSON.parse(impersonatingData) : null;

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Flex align="center" justify="center" minH="80vh">
        <Box 
          maxW="xl" 
          w="100%" 
          p={8} 
          borderWidth={1} 
          borderRadius="lg" 
          boxShadow="lg"
          bg="#1A202C"
          color="white"
        >
          <Alert status="error" bg="#2D3748" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Access Denied: Admin privileges required to access this page.
            </AlertDescription>
          </Alert>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" minH="80vh">
      <Box 
        maxW="xl" 
        w="100%" 
        p={8} 
        borderWidth={1} 
        borderRadius="lg" 
        boxShadow="lg"
        bg="#1A202C"
        color="white"
      >
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color="#e0e0e8">Admin Panel</Heading>
          
          <Text color="gray.400">
            View any user's account by entering their email address below.
          </Text>

          {isImpersonating && (
            <>
              <Alert status="warning" bg="#2D3748" borderRadius="md">
                <AlertIcon />
                <Box flex="1">
                  <Text fontWeight="bold">Currently Impersonating</Text>
                  <Text fontSize="sm">
                    {impersonationInfo.name} ({impersonationInfo.email})
                  </Text>
                </Box>
              </Alert>

              <Button
                leftIcon={<FiLogIn />}
                variant="solid"
                bg="orange.600"
                color="white"
                _hover={{ bg: "orange.700" }}
                onClick={handleExitImpersonation}
                size="md"
              >
                Exit Impersonation
              </Button>

              <Divider borderColor="gray.600" />
            </>
          )}

          <form onSubmit={handleImpersonate}>
            <VStack spacing={4} align="stretch">
              <FormControl id="user_email" isRequired>
                <FormLabel color="gray.300">User Email</FormLabel>
                <Input 
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="user@example.com"
                  bg="#2D3748"
                  borderColor="gray.600"
                  color="#e0e0e8"
                  _hover={{ borderColor: "gray.500" }}
                  _focus={{ borderColor: "rgba(65, 105, 225, 0.8)", boxShadow: "0 0 0 1px rgba(65, 105, 225, 0.8)" }}
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>

              <Button
                type="submit"
                leftIcon={<FiSearch />}
                variant="solid"
                bg="rgba(65, 105, 225, 0.8)"
                color="white"
                _hover={{ bg: "rgba(65, 105, 225, 1)" }}
                isLoading={loading}
                loadingText="Impersonating..."
                size="lg"
                mt={2}
              >
                View User Account
              </Button>
            </VStack>
          </form>

          <Divider borderColor="gray.600" />

          <Box>
            <Heading size="sm" color="gray.400" mb={2}>How it works:</Heading>
            <VStack align="stretch" spacing={2} fontSize="sm" color="gray.400">
              <Text>1. Enter a user's email address</Text>
              <Text>2. Click "View User Account" to see their campaigns</Text>
              <Text>3. You'll be viewing the app exactly as they see it</Text>
              <Text>4. All impersonation actions are logged for security</Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default AdminPage;

